'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { LanguageSelector } from '@/components/layout/language-selector';
import { authClient } from '@/lib/auth/client';

export function Navbar() {
  const t = useTranslations('landing.nav');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { data: session, isPending } = authClient.useSession();

  const navigation = [
    { name: t('features'), href: '#features' },
    { name: t('howItWorks'), href: '#how-it-works' },
    { name: t('pricing'), href: '#pricing' },
  ];

  // Determine dashboard link based on user role
  const getDashboardLink = () => {
    if (!session?.user?.role) return '/dashboard';
    const role = session.user.role;
    if (role.includes('vendor')) return '/vendor/dashboard';
    if (role.includes('platform_admin')) return '/platform-admin/dashboard';
    return '/dashboard'; // producer/org_admin
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 start-0 end-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'glass border-b border-border/50 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <nav className="container-landing">
        <div className="flex h-16 md:h-20 items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 group">
            <div className="relative h-14 md:h-16">
              <img
                src="/logo.png"
                alt="Tarboot - Capture Culture"
                className="h-full w-auto object-contain transition-opacity group-hover:opacity-90"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted/50"
              >
                {item.name}
              </a>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSelector />
            {session?.user ? (
              // Logged in - show Dashboard button
              <Button
                size="sm"
                className="font-medium shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-shadow"
                disabled={isPending}
                asChild={!isPending}
              >
                {isPending ? (
                  'Dashboard'
                ) : (
                  <a href={getDashboardLink()}>Dashboard</a>
                )}
              </Button>
            ) : (
              // Not logged in - show Login and Sign Up
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="font-medium"
                  disabled={isPending}
                  asChild={!isPending}
                >
                  {isPending ? (
                    t('login')
                  ) : (
                    <Link href="/login">{t('login')}</Link>
                  )}
                </Button>
                <Button
                  size="sm"
                  className="font-medium shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-shadow"
                  disabled={isPending}
                  asChild={!isPending}
                >
                  {isPending ? (
                    t('getStarted')
                  ) : (
                    <Link href="/signup">{t('getStarted')}</Link>
                  )}
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu */}
          <div className="flex md:hidden items-center gap-2">
            <LanguageSelector />
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[350px]">
                <div className="flex flex-col gap-6 mt-8">
                  <nav className="flex flex-col gap-1">
                    {navigation.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        onClick={() => setIsOpen(false)}
                        className="px-4 py-3 text-base font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
                      >
                        {item.name}
                      </a>
                    ))}
                  </nav>
                  <div className="border-t pt-6 flex flex-col gap-3">
                    {session?.user ? (
                      // Logged in - show Dashboard button
                      <Button
                        className="w-full justify-center shadow-lg shadow-primary/25"
                        disabled={isPending}
                        asChild={!isPending}
                      >
                        {isPending ? (
                          'Dashboard'
                        ) : (
                          <a href={getDashboardLink()}>Dashboard</a>
                        )}
                      </Button>
                    ) : (
                      // Not logged in - show Login and Sign Up
                      <>
                        <Button
                          variant="outline"
                          className="w-full justify-center"
                          disabled={isPending}
                          asChild={!isPending}
                        >
                          {isPending ? (
                            t('login')
                          ) : (
                            <Link href="/login">{t('login')}</Link>
                          )}
                        </Button>
                        <Button
                          className="w-full justify-center shadow-lg shadow-primary/25"
                          disabled={isPending}
                          asChild={!isPending}
                        >
                          {isPending ? (
                            t('getStarted')
                          ) : (
                            <Link href="/signup">{t('getStarted')}</Link>
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
}
